import React, { createContext, useContext, useEffect } from "react";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { AuthContext } from "./AuthContext";
import ConstConfig from "../configs/constConfig";
import CryptoJS from "crypto-js";

const AxiosContext = createContext();
const { Provider } = AxiosContext;
const MainUrl = ConstConfig.MAIN_URL;
const AxiosProvider = ({ children }) => {
  const {
    getAccessToken,
    authUserRole,
    setUser,
    socket,
    app_js_intialLoad,
    setAgoraAppId,
    setAppleClientId,
    setFacebookAppId,
    setGoogleClientId,
    setSessionModalShow,
    setLoading,
    setShowTimezoneChangeAlert,
    setIsInCall,
    setIsCallMinimized,
    resetCallState,
    setCallMetadata,
  } = useContext(AuthContext);

  useEffect(() => {
    set_initial_tokens_Fun();
  }, []);

  const IsTherapist = authUserRole == "Therapist";
  let isLoggingOut = false;

  const userAxios = axios.create({
    baseURL: MainUrl,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
  const authFormDataAxios = axios.create({
    baseURL: MainUrl,
    headers: {
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });

  const userFormDataAxios = axios.create({
    baseURL: MainUrl,
    headers: {
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });

  const authAxios = axios.create({
    baseURL: MainUrl,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });

  const enctyptDataFun = (data) => {
    const endata = CryptoJS.AES.encrypt(
      JSON.stringify(data),
      process.env.REACT_APP_CRYPTO_KEY
    ).toString();
    return endata;
  };

  // const enctyptTokenOrKey = (data, token1, token2) => {
  //   const encryptKey = CryptoJS.AES.encrypt(data, token2).toString();

  //   const secondEncrypt = CryptoJS.AES.encrypt(encryptKey, token1).toString();

  //   return secondEncrypt;
  // };

  const get_user_details = async () => {
    try {
      const res = await axios.get(`/therapist/fetch_therapist_profile`, {
        baseURL: MainUrl,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      return res?.data;
    } catch (err) {
      console.log("error in get_user_details api = ", err);
    }
  };

  const referesh_token = async () => {
    const res = await userAxios.get(`/auth/get_auth_referesh_token`);

    return res?.data;
  };

  const get_patient_user_details = async () => {
    try {
      const res = await axios.get(`/patient/get_profile`, {
        baseURL: MainUrl,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });
      return res?.data;
    } catch (err) {
      console.log("error in get_patient_user_details_api = ", err);
    }
  };

  const set_user_details = async () => {
    const res = await get_user_details();

    if (res?.status === 1) {
      localStorage.setItem("E_C_U_D", enctyptDataFun(res.data));
      // Set Sentry user context on login
      Sentry.setUser({
        id: res.data._id,
        email: res.data.email,
        username: res.data.first_name ? `${res.data.first_name} ${res.data.last_name || ""}`.trim() : undefined,
      });
    }
  };
  const set_patient_user_details = async () => {
    try {
      const res = await get_patient_user_details();
      if (res?.status === 1) {
        localStorage.setItem("E_C_U_D", enctyptDataFun(res.data));
        // Set Sentry user context on login
        Sentry.setUser({
          id: res.data._id,
          email: res.data.email,
          username: res.data.first_name ? `${res.data.first_name} ${res.data.last_name || ""}`.trim() : undefined,
        });
      }
    } catch (err) {
      console.log("error in set_patient_user_details = ", err);
    }
  };

  const log_out = async () => {
    try {
      const res = await userAxios.post(`/auth/logout`);
      return res?.data;
    } catch (err) {
      console.log("error in log_out = ", err);
    }
  };

  // Helper function to reset all call states on logout
  const resetAllCallStates = () => {
    Sentry.setUser(null); // Clear Sentry user context on logout
    setIsInCall(false);
    setIsCallMinimized(false);
    resetCallState(); // Reset unified call state (stops timer)
    setCallMetadata({
      callType: "",
      sendingCall: false,
      receivingCall: false,
      mute_audio: false,
      hide_video: false,
      showMessage: "",
      callDuration: 0,
      localVideoTrack: null,
      remoteVideoTrack: null,
      localAudioTrack: null,
      remoteAudioTrack: null,
      userName: "",
      chat_id: null,
      remoteUserId: null,
    });
  };

  authAxios.interceptors.request.use(
    (config) => {
      if (getAccessToken()) {
        IsTherapist ? set_user_details() : set_patient_user_details();
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  authFormDataAxios.interceptors.request.use(
    (config) => {
      if (getAccessToken()) {
        IsTherapist ? set_user_details() : set_patient_user_details();
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  userAxios.interceptors.request.use(
    (config) => {
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
        config.headers.time_zone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      if (getAccessToken()) {
        IsTherapist ? set_user_details() : set_patient_user_details();
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  userAxios.interceptors.response.use(
    async (config) => {
      if (config.data) {
        if (config.data.status == 10 && !isLoggingOut) {
          isLoggingOut = true;
          try {
            const res = await log_out();
            if (res?.status === 1) {
              localStorage.clear();
              socket && socket.disconnect();
              app_js_intialLoad.current = false;

              // FIX: Reset all call states to remove PiP on session expiry
              resetAllCallStates();

              setUser({
                access_token: null,
                details: null,
              });
              setTimeout(() => {
                isLoggingOut = false;
              }, 3000);

              return Promise.reject("User logged out");
            }
          } catch (err) {
            isLoggingOut = false;
          }
        } else if (config.data.status == 5) {
          console.log("config.data123 =", config.data);
          setLoading(false);
          setShowTimezoneChangeAlert(true);
        }
      }

      return config;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const resp = await referesh_token();
          const access_token = resp?.data?.token;
          setUser((previous_state) => ({
            ...previous_state,
            access_token: access_token,
          }));
          localStorage.setItem("E_C_U_A_T", enctyptDataFun(access_token));
          userAxios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${access_token}`;
          originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
          return userAxios(originalRequest);
        } catch (err) {
          await log_out();
          localStorage.clear();
          socket && socket.disconnect();
          app_js_intialLoad.current = false;

          // FIX: Reset all call states to remove PiP on token refresh failure
          resetAllCallStates();

          setUser({
            access_token: null,
            details: null,
          });
          setSessionModalShow(true);
          setTimeout(() => {
            isLoggingOut = false;
          }, 3000);
          return;
        }
      }
      return Promise.reject(error);
    }
  );

  userFormDataAxios.interceptors.request.use(
    (config) => {
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${getAccessToken()}`;
        config.headers.time_zone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      if (getAccessToken()) {
        IsTherapist ? set_user_details() : set_patient_user_details();
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  userFormDataAxios.interceptors.response.use(
    async (config) => {
      if (config.data) {
        if (config.data.status == 10 && !isLoggingOut) {
          isLoggingOut = true;
          try {
            const res = await log_out();
            if (res?.status === 1) {
              localStorage.clear();
              socket && socket.disconnect();
              app_js_intialLoad.current = false;

              // FIX: Reset all call states to remove PiP on session expiry
              resetAllCallStates();

              setUser({
                access_token: null,
                details: null,
              });
              setTimeout(() => {
                isLoggingOut = false;
              }, 3000);

              return Promise.reject("User logged out");
            }
          } catch (err) {
            isLoggingOut = false;
          }
        } else if (config.data.status == 5) {
          setLoading(false);
          setShowTimezoneChangeAlert(true);
        }
      }

      return config;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const resp = await referesh_token();
          const access_token = resp?.data?.token;

          setUser((previous_state) => ({
            ...previous_state,
            access_token: access_token,
          }));
          localStorage.setItem("E_C_U_A_T", enctyptDataFun(access_token));
          userFormDataAxios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${access_token}`;
          originalRequest.headers["Authorization"] = `Bearer ${access_token}`;
          return userFormDataAxios(originalRequest);
        } catch {
          await log_out();
          localStorage.clear();
          socket && socket.disconnect();
          app_js_intialLoad.current = false;

          // Clear Sentry user + reset call states on token refresh failure
          resetAllCallStates();

          setUser({
            access_token: null,
            details: null,
          });
          setSessionModalShow(true);
          setTimeout(() => {
            isLoggingOut = false;
          }, 3000);
          return;
        }
      }
      return Promise.reject(error);
    }
  );

  async function set_initial_tokens_Fun() {
    try {
      const token_res = await authAxios.get("/auth/token_1");
      const tokenRes = token_res?.data;
      if (tokenRes?.status === 1) {
        const token1 = tokenRes?.data;
        //  AOGRA APP ID FIRST STEP
        const first_agora_app_id_bytes = CryptoJS.AES.decrypt(
          process.env.REACT_APP_AGORA_APP_ID,
          token1
        );
        const decrypted_first_agora_app_id_token =
          first_agora_app_id_bytes.toString(CryptoJS.enc.Utf8);

        // APPLE CLIENT ID FIRST STEP
        const first_apple_clientid_bytes = CryptoJS.AES.decrypt(
          process.env.REACT_APP_APPLE_CLIENT_ID,
          token1
        );
        const decrypted_first_apple_clientid_token =
          first_apple_clientid_bytes.toString(CryptoJS.enc.Utf8);

        // FACEBOOK APP ID FIRST STEP
        const first_facebook_appid_bytes = CryptoJS.AES.decrypt(
          process.env.REACT_APP_FACEBOOK_APP_ID,
          token1
        );
        const decrypted_first_facebook_appid_token =
          first_facebook_appid_bytes.toString(CryptoJS.enc.Utf8);

        // GOOGLE CLIENT ID FIRST STEP
        const first_google_clientid_bytes = CryptoJS.AES.decrypt(
          process.env.REACT_APP_GOOGLE_CLIENT_ID,
          token1
        );
        const decrypted_first_google_clientid_token =
          first_google_clientid_bytes.toString(CryptoJS.enc.Utf8);

        const token2_res = await authAxios.get("/auth/token_2");
        const token2Res = token2_res?.data;
        if (token2Res?.status === 1) {
          const token2 = token2Res?.data;
          //  AOGRA APP ID SECOND STEP
          const second_agora_app_id_bytes = CryptoJS.AES.decrypt(
            decrypted_first_agora_app_id_token,
            token2
          );
          const decrypted_agora_app_id_data =
            second_agora_app_id_bytes.toString(CryptoJS.enc.Utf8);
          setAgoraAppId(decrypted_agora_app_id_data);

          // APPLE CLIENT ID SECOND STEP

          const second_apple_clientid_bytes = CryptoJS.AES.decrypt(
            decrypted_first_apple_clientid_token,
            token2
          );
          const decrypted_apple_clientId_data =
            second_apple_clientid_bytes.toString(CryptoJS.enc.Utf8);

          setAppleClientId(decrypted_apple_clientId_data);

          // FACEBOOK APP ID SECOND STEP

          const second_facebook_appid_bytes = CryptoJS.AES.decrypt(
            decrypted_first_facebook_appid_token,
            token2
          );
          const decrypted_facebook_appid_data =
            second_facebook_appid_bytes.toString(CryptoJS.enc.Utf8);
          setFacebookAppId(decrypted_facebook_appid_data);

          // GOOGLE CLIENT ID SECOND STEP

          const second_google_clientid_bytes = CryptoJS.AES.decrypt(
            decrypted_first_google_clientid_token,
            token2
          );
          const decrypted_google_clientId_data =
            second_google_clientid_bytes.toString(CryptoJS.enc.Utf8);
          setGoogleClientId(decrypted_google_clientId_data);
        }
      }
    } catch (err) {
      console.log("error in Token fun =", err);
    }
  }

  return (
    <Provider
      value={{
        authAxios,
        userAxios,
        authFormDataAxios,
        userFormDataAxios,
      }}
    >
      {children}
    </Provider>
  );
};

export { AxiosContext, AxiosProvider };
